import { AfterViewInit, ChangeDetectorRef, Component, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { AssessmentTileComponent as AssessmentTileComponent } from "../../components/assessment-tile/assessment-tile.component";
import { DynamicListComponent, DynamicListEvent, PaginationListItem } from "../../components/dynamic-list/dynamic-list.component";
import { Pagination, PaginationOptions } from "../../services/pagination.service";
import { BehaviorSubject, combineLatest, Subscription } from "rxjs";
import { map, shareReplay, switchMap, take } from "rxjs/operators";
import { BusinessService } from "../../services/business.service";
import { SupabaseService } from "../../services/supabase.service";
import { LobbyService } from "../../services/lobby.service";
import { ViewAsService } from "../../services/view-as.service";
import { limitStep } from "../../services/items.service";
import { RealtimePostgresChangesPayload } from "@supabase/supabase-js";
import { Assessment, AssessmentTileData, AssessmentTimeRange } from "../../models/assessment.model";
import { ScheduleService, ScheduleTileData } from "../../services/schedule.service";
import { dateString } from "projects/common/src/lib/services";
import { Client } from 'projects/common/src/public-api';
import { TimeRange } from 'projects/common/src/lib/models/time-range.model';
import { Router } from "@angular/router";
import { Workflow } from '../../models/workflow.model';
import { frontToServerTranslation } from 'projects/common/src/lib/services/supabase.service';

function assessmentsToPaginationList(assessments: AssessmentTileData[]) {
    return assessments.map(assessment => ({
        componentClass: AssessmentTileComponent,
        header: dateString(assessment.startTime, assessment.endTime),
        args: { document: {...assessment, docType: 'Assessment'} }
    }));
}

@Component({
    selector: 'app-assessments',
    templateUrl: './assessments.component.html',
    styleUrls: ['./assessments.component.scss']
})
export class AssessmentsComponent implements OnInit, AfterViewInit, OnDestroy {

    @ViewChild('list') dynamicList!: DynamicListComponent<AssessmentTileComponent>;
    @ViewChild('dateFilterList') dateFilterList!: DynamicListComponent<AssessmentTileComponent>;

    pagination!: Pagination<AssessmentTileData>;
    dateFilterPagination!: Pagination<AssessmentTileData>;
    loading$ = new BehaviorSubject<boolean>(true);
    dateFilterLoading$ = new BehaviorSubject<boolean>(true);

    firstHeaderRangeSubject = new BehaviorSubject<TimeRange | null>(null);
    firstHeader$ = this.firstHeaderRangeSubject.pipe(map(range => range ? dateString(range?.startTime, range?.endTime): null));
    filterFirstHeaderRangeSubject = new BehaviorSubject<TimeRange | null>(null);
    filterFirstHeader$ = this.filterFirstHeaderRangeSubject.pipe(map(range => range ? dateString(range?.startTime, range?.endTime): null));
    initialTopItems!: PaginationListItem<AssessmentTileComponent>[];
    initialBottomItems!: PaginationListItem<AssessmentTileComponent>[];
    initialFilterTopItems!: PaginationListItem<AssessmentTileComponent>[];
    initialFilterBottomItems!: PaginationListItem<AssessmentTileComponent>[];
    initialFilterCanLoadMoreTop = true;
    initialFilterCanLoadMoreBottom = true;

    currentStatusFilter$ = this.scheduleService.filterAssessmentStatus$;
    currentStatusFilterSubject = this.scheduleService.filterAssessmentStatusSubject;

    dateFilter$ = combineLatest([this.lobbyService.selectedDateRange$, this.currentStatusFilter$]).pipe(
        switchMap(async ([range, status]) => {
            if(!range) 
                return null;
            this.dateFilterLoading$.next(true);
            const inLimit = limitStep;
            const options: PaginationOptions = {
                inLimit, 
                inOffset: 0, 
                inStartDate: frontToServerTranslation(range.from), 
                inEndDate: frontToServerTranslation(range.to),
            };
            const userIds = await this.viewAsService.selectedUsersIds$.pipe(take(1)).toPromise();
            if(userIds)
                options.inUserIds = userIds;
            if(status !== 'All')
                (options as any).inStatus = status.toLowerCase();
            
            const [past, future] = await Promise.all([
                this.dateFilterPagination.get({...options, in_direction: 'past'}),
                this.dateFilterPagination.get({...options, in_direction: 'future'})
            ]);
            this.filterFirstHeaderRangeSubject.next(future.length ? { startTime: future[0].startTime, endTime: future[0].endTime } : null);
            this.initialFilterTopItems = assessmentsToPaginationList(past);
            this.initialFilterBottomItems = assessmentsToPaginationList(future);
            this.initialFilterCanLoadMoreBottom = future.length >= inLimit;
            this.initialFilterCanLoadMoreTop = past.length >= inLimit;
            this.dateFilterLoading$.next(false);
            return range;
        }),
        shareReplay({ bufferSize: 1, refCount: true})
    );

    statusFilterSub?: Subscription;

    constructor(
        private businessService: BusinessService,
        private supabaseService: SupabaseService,
        private lobbyService: LobbyService,
        private viewAsService: ViewAsService,
        private scheduleService: ScheduleService,
        private router: Router,
        private cd: ChangeDetectorRef
    ) {}

    async ngOnInit() {
        const business = (await this.businessService.selectedBusiness$.pipe(take(1)).toPromise())!;
        this.pagination = new Pagination<AssessmentTileData, Assessment | Client | AssessmentTimeRange | Workflow>(this.supabaseService, 'get_assessments', business.businessId, 'assessments')
            .on<Assessment, 'UPDATE'>('UPDATE', 'assessment', this.handleAssessmentChanges.bind(this))
            .on<Client, 'UPDATE'>('UPDATE', 'assessment_client', this.handleClientChanges.bind(this))
            .on<AssessmentTimeRange>('*', 'assessment_range', this.handleRangeChanges.bind(this))
            .on<Workflow, 'UPDATE'>('UPDATE', 'workflow', this.handleWorkflowChanges.bind(this));

        this.dateFilterPagination = new Pagination<AssessmentTileData, Assessment | Client | AssessmentTimeRange | Workflow>(this.supabaseService, 'get_assessments', business.businessId, 'filtered_assessments')
            .on<Assessment, 'UPDATE'>('UPDATE', 'assessment', this.handleDateFilterAssessmentChanges.bind(this))
            .on<Client, 'UPDATE'>('UPDATE', 'assessment_client', this.handleDateFilterClientChanges.bind(this))
            .on<AssessmentTimeRange>('*', 'assessment_range', this.handleDateFilterRangeChanges.bind(this))
            .on<Workflow, 'UPDATE'>('UPDATE', 'workflow', this.handleDateFilterWorkflowChanges.bind(this));

        this.statusFilterSub = this.currentStatusFilter$.subscribe(_ => this.loadInitialItems());
    }

    ngAfterViewInit(): void {
        this.setInitialCanLoadMore();
    }
    
    ngOnDestroy(): void {
        this.pagination.destroy();
        this.dateFilterPagination.destroy();
        this.statusFilterSub?.unsubscribe();
    }

    setInitialCanLoadMore() {
        if (!this.initialTopItems || !this.initialBottomItems || !this.dynamicList)
            return;

        if(this.initialTopItems.length < limitStep)
            this.dynamicList.canLoadMoreTop = false;
        if(this.initialBottomItems.length < limitStep)
            this.dynamicList.canLoadMoreBottom = false;
    }

    async loadInitialItems() {
        this.loading$.next(true);
        const inLimit = limitStep;
        const options: PaginationOptions = { inLimit, inOffset: 0 };
        const userIds = await this.viewAsService.selectedUsersIds$.pipe(take(1)).toPromise();
        if(userIds)
            options.inUserIds = userIds;
        const status = this.scheduleService.filterAssessmentStatusSubject.value;
        if(status !== 'All')
            (options as any).inStatus = status.toLowerCase();
        
        const [past, future] = await Promise.all([
            this.pagination.get({...options, in_direction: 'past'}),
            this.pagination.get({...options, in_direction: 'future'})
        ]);
        this.firstHeaderRangeSubject.next(future.length ? { startTime: future[0].startTime, endTime: future[0].endTime } : null);
        this.initialTopItems = assessmentsToPaginationList(past);
        this.initialBottomItems = assessmentsToPaginationList(future);
        this.setInitialCanLoadMore();
        this.loading$.next(false);
        this.cd.detectChanges();
    }

    async loadItems(at: 'top' | 'bottom', inOffset = 0) {
        const inLimit = limitStep;
        const options: PaginationOptions = { inLimit, inOffset, inDirection: at === 'top' ? 'past' : 'future' };
        const userIds = await this.viewAsService.selectedUsersIds$.pipe(take(1)).toPromise();
        if(userIds)
            options.inUserIds = userIds;
        const status = this.scheduleService.filterAssessmentStatusSubject.value;
        if(status !== 'All')
            (options as any).inStatus = status.toLowerCase();

        const assessments = await this.pagination.get(options);
        if(at === 'top') {
            if(assessments.length < inLimit)
                this.dynamicList.canLoadMoreTop = false;
            this.dynamicList.addAtTop(
                assessmentsToPaginationList(assessments)
            );
        } else {
            if(assessments.length < inLimit)
                this.dynamicList.canLoadMoreBottom = false;
            this.dynamicList.addAtBottom(
                assessmentsToPaginationList(assessments)
            );
        }
    }

    async loadDateFilterItems(at: 'top' | 'bottom', inOffset = 0) {
        const inLimit = limitStep;
        const range = await this.lobbyService.selectedDateRange$.pipe(take(1)).toPromise();
        const options: PaginationOptions = {
            inLimit,
            inOffset,
            inDirection: at === 'top' ? 'past' : 'future',
            inStartDate: frontToServerTranslation(range!.from),
            inEndDate: frontToServerTranslation(range!.to),
        };
        const userIds = await this.viewAsService.selectedUsersIds$.pipe(take(1)).toPromise();
        if(userIds)
            options.inUserIds = userIds;
        const status = this.scheduleService.filterAssessmentStatusSubject.value;
        if(status !== 'All')
            (options as any).inStatus = status.toLowerCase();

        const assessments = await this.dateFilterPagination.get(options);
        if(!this.dateFilterList)
            return;

        if(at === 'top') {
            if(assessments.length < inLimit)
                this.dateFilterList.canLoadMoreTop = false;
            this.dateFilterList.addAtTop(
                assessmentsToPaginationList(assessments)
            );
        } else {
            if(assessments.length < inLimit)
                this.dateFilterList.canLoadMoreBottom = false;
            this.dateFilterList.addAtBottom(
                assessmentsToPaginationList(assessments)
            );
        }
    }

    handleAssessmentChanges(changes: RealtimePostgresChangesPayload<Assessment>) {
        this.scheduleService.handleAssessmentChanges(this.dynamicList, changes, this.firstHeaderRangeSubject, this.currentStatusFilterSubject.value);
    }

    handleClientChanges(changes: RealtimePostgresChangesPayload<Client>) {
        this.scheduleService.handleAssessmentClientChanges(this.dynamicList, changes);
    }

    handleRangeChanges(changes: RealtimePostgresChangesPayload<AssessmentTimeRange>) {
        this.scheduleService.handleAssessmentRangeChanges(this.dynamicList, changes, this.firstHeaderRangeSubject, this.currentStatusFilterSubject.value);
    }
    
    handleWorkflowChanges(changes: RealtimePostgresChangesPayload<Workflow>) {
        this.scheduleService.handleWorkflowChanges(this.dynamicList, changes);
    }

    handleDateFilterAssessmentChanges(changes: RealtimePostgresChangesPayload<Assessment>) {
        this.scheduleService.handleDateFilterAssessmentChanges(this.dateFilterList, changes, this.filterFirstHeaderRangeSubject, this.currentStatusFilterSubject.value);
    }

    handleDateFilterClientChanges(changes: RealtimePostgresChangesPayload<Client>) {
        this.scheduleService.handleDateFilterAssessmentClientChanges(this.dateFilterList, changes);
    }

    handleDateFilterRangeChanges(changes: RealtimePostgresChangesPayload<AssessmentTimeRange>) {
        this.scheduleService.handleDateFilterAssessmentRangeChanges(this.dateFilterList, changes, this.filterFirstHeaderRangeSubject, this.currentStatusFilterSubject.value);
    }

    handleDateFilterWorkflowChanges(changes: RealtimePostgresChangesPayload<Workflow>) {
        this.scheduleService.handleDateFilterWorkflowChanges(this.dateFilterList, changes);
    }

    onItemEvent(event: DynamicListEvent<AssessmentTileData>) {
        this.scheduleService.onItemEvent(event as DynamicListEvent<ScheduleTileData>);
    }
}
